import React, { useState, useEffect } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { useGetDealerDropdownListQuery } from '../../api/endpoints/dealer';
 
/**
 * Dealer dropdown component with search functionality
 * @param {Object} props
 * @param {string|Object} props.value - The selected value
 * @param {Function} props.onChange - Callback when selection changes
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @param {string} props.placeholder - Placeholder text
 * @param {number} props.rowCount - Number of rows to fetch
 */
const DealerDropdown = ({ value, onChange, className = '', disabled = false, placeholder = 'Bayi seçiniz veya arayınız', rowCount = 10 }) => {
    const [selectedDealer, setSelectedDealer] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [dropdownKey, setDropdownKey] = useState(0);
 
    // Get search text - only use searchText state, not value prop
    // This prevents API calls when value changes and ensures selected value is preserved
    const getSearchText = () => {
        // Only use searchText state for API calls
        // Don't use value prop to avoid unnecessary API calls and preserve selected value
        return searchText.trim();
    };
 
    // Fetch dealer list from API with search parameter
    const { data: dealerList = [], isLoading: isLoadingDealers } = useGetDealerDropdownListQuery({
        RowCount: rowCount || 50, // Reduce the number of items loaded by default
        SearchText: getSearchText()
    });
 
    // Transform dealer list for dropdown
    let dealerOptions = dealerList.map((dealer) => {
        const erpDealerId = dealer.dealerId;
 
        const option = {
            id: dealer.dealerId,
            name: dealer.name,
            text: dealer.translatedText || dealer.name,
            erpDealerId: erpDealerId,
            displayValue: dealer.name // Use the name as display value
        };
       
        return option;
    });
 
    // If selectedDealer exists but is not in dealerOptions, add it to the list
    // This ensures the selected value is always visible in the dropdown
    if (selectedDealer && !dealerOptions.find(option => option.id?.toString() === selectedDealer.id?.toString())) {
        dealerOptions = [selectedDealer, ...dealerOptions];
    }
 
    // Handle selection change
    const handleSelectionChange = (e) => {
        setSelectedDealer(e.value);
        onChange(e.value || null);
    };
 
    // Handle filter change
    const handleFilterChange = (e) => {
        setSearchText(e.filter);
    };
 
    // Handle dropdown hide - reset search text and force re-render
    const handleDropdownHide = () => {
        setSearchText('');
        // Force dropdown to re-render with clean state
        setDropdownKey(prev => prev + 1);
    };
 
    // Set initial value if available
    useEffect(() => {
        if (value) {
            // If value is already an object with the expected structure
            if (typeof value === 'object' && value.id) {
                // Check if current selectedDealer matches value
                const currentId = selectedDealer?.id?.toString();
                const valueId = value.id?.toString();
                if (currentId !== valueId) {
                    // Try to find in dealerOptions first (if available)
                    const found = dealerOptions.find((option) =>
                        option.id?.toString() === value.id?.toString()
                    );
                    // Use found option if available, otherwise use value directly
                    // This ensures selected value is preserved even if not in current API response
                    setSelectedDealer(found || value);
                }
            } else if (typeof value === 'string' || typeof value === 'number') {
                // If value is a string or number, try to find matching option
                if (dealerOptions.length > 0) {
                    const found = dealerOptions.find((option) =>
                        option.displayValue === value ||
                        option.name === value ||
                        option.id?.toString() === value.toString() ||
                        option.erpDealerId?.toString() === value.toString()
                    );
                    if (found && (!selectedDealer || selectedDealer.id?.toString() !== found.id?.toString())) {
                        setSelectedDealer(found);
                    }
                }
            }
        } else if (!value && selectedDealer) {
            // Clear selection if value is null/undefined
            setSelectedDealer(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, dealerOptions]);
 
    return (
        <Dropdown
            key={dropdownKey}
            value={selectedDealer}
            options={dealerOptions}
            onChange={handleSelectionChange}
            onFilter={handleFilterChange}
            onHide={handleDropdownHide}
            optionLabel="displayValue"
            filter
            showClear
            filterBy="erpDealerId,name,text"
            className={className}
            placeholder={placeholder}
            loading={isLoadingDealers}
            disabled={disabled}
            virtualScrollerOptions={{ itemSize: 38 }}
            itemTemplate={(option) => (
                <div>
                    <span style={{ fontWeight: 'bold' }}>{option.erpDealerId}</span> {option.text}
                </div>
            )}
        />
    );
};
 
export default DealerDropdown;